import React, { useState } from "react";
import { useHotel } from "@/hooks/useHotel";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RoomStatus, Room } from "@/types/room";
import type { DateRange } from "@/hooks/useDateRange";
import RoomFilter from "./rooms/components/RoomFilter";
import { Edit, Trash2, Image, Plus, QrCode } from "lucide-react";
import { SubNavigation } from "./components/SubNavigation";
import { useRoomDelete } from "./rooms/hooks/useRoomDelete";
import { useRoomFiltering } from "@/hooks/useRoomFiltering";
import { DataTable, Column } from "@/components/ui/data-table";
import { RoomStatusLabel } from "./rooms/components/StatusLabel";
import { ReservationNavigation } from "./components/ReservationNavigation";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { RoomQrDialog } from "./rooms/components/RoomQrDialog";
import { createDefaultRoomUsageDateRange } from "@/lib/roomUsage";
import { RoomsAnalyticsOverview } from "./rooms/components/RoomsAnalyticsOverview";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

export const RoomsPage = () => {
  const { isLoading, error } = useHotel();
  const hotelId = useCurrentHotelId();

  if (isLoading && !hotelId) {
    return <div>Loading</div>;
  }

  if (error) {
    return (
      <div className="py-4 text-center text-red-600">
        Error loading hotel: {error.message}
      </div>
    );
  }

  if (!hotelId) {
    return <div>Loading</div>;
  }

  return <HotelRooms hotelId={hotelId} />;
};

export const HotelRooms: React.FC<{ hotelId: string }> = ({ hotelId }) => {
  const navigate = useNavigate();
  const { hasReservationsAccess } = useAccessCapabilities();
  const [activeTab] = useState<"rooms" | "categories">("rooms");
  const [usageDateRange, setUsageDateRange] = useState<DateRange>(() =>
    createDefaultRoomUsageDateRange(),
  );

  // Define tabs for SubNavigation
  const subNavigationTabs = [
    { id: "rooms", label: "Rooms" },
    { id: "categories", label: "Categories" },
  ];

  const { rooms, isLoading, error, filters, actions, pagination } =
    useRoomFiltering(hotelId!);

  const { handleRoomDelete, isLoading: isLoadingDeletion } = useRoomDelete();

  const columns: Column<Room>[] = [
    {
      key: "status",
      header: "Status",
      className: "text-center",
      render: (value: RoomStatus) => <RoomStatusLabel status={value} />,
      sortable: true,
    },
    {
      key: "photo",
      header: "Photo",
      className: "text-center flex justify-center items-center",
      render: (_, room: Room) => (
        <div className="flex h-12 w-12 items-center justify-center rounded border border-gray-300 bg-gray-100">
          <PresignedImageWithPlaceholder
            className={"h-10 w-10 overflow-hidden object-cover"}
            photo={room.photoId}
            placeholder={<Image className="h-6 w-6 text-gray-400" />}
          />
        </div>
      ),
    },
    {
      key: "categoryId",
      header: "Category",
      className: "text-center",
      render: (_, room: Room) => (
        <span className="text-sm">{room.category?.name}</span>
      ),
      sortable: true,
    },
    {
      key: "amenities",
      header: "PMS Category",
      className: "text-center",
      render: (_, room: Room) => <span>{room?.category?.pmsType ?? ""}</span>,
    },
    {
      key: "number",
      header: "Room",
      className: "text-center",
      sortable: true,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: "floor",
      header: "Floor",
      className: "text-center",
      sortable: true,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-2">
          <RoomQrDialog
            room={row}
            hotelId={hotelId}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={(event) => event.stopPropagation()}
              >
                <QrCode className="h-4 w-4" />
              </Button>
            }
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => navigate(`/reservations/rooms/${row.id}`)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleDeleteRoom(row.id)}
            disabled={isLoadingDeletion}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Handle room deletion
  const handleDeleteRoom = async (roomId: string) => {
    if (window.confirm("Are you sure you want to delete this room?")) {
      try {
        await handleRoomDelete(roomId, hotelId);
      } catch (error) {
        console.error("Failed to delete room:", error);
      }
    }
  };

  const onTabChange = (tab: string) => {
    if (tab === "categories") {
      navigate("/reservations/rooms/categories");
    }
  };

  return (
    <div className="space-y-6 p-12">
      {/* Navigation Tabs */}
      <ReservationNavigation activeTab="rooms" />

      {/* Sub Navigation */}
      <SubNavigation
        activeTab={activeTab}
        onTabChange={onTabChange}
        tabs={subNavigationTabs}
      />

      {hasReservationsAccess ? (
        <RoomsAnalyticsOverview
          hotelId={hotelId}
          dateRange={usageDateRange}
          onDateRangeChange={setUsageDateRange}
        />
      ) : null}

      {/* Filters and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <RoomFilter hotelId={hotelId} actions={actions} filters={filters} />
        </div>

        <Button
          variant="new_default"
          onClick={() => navigate("/reservations/rooms/add")}
        >
          <Plus />
          Add room
        </Button>
      </div>

      {/* Data Table */}
      <DataTable
        data={rooms}
        columns={columns}
        loading={isLoading}
        pagination={{
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          onPageChange: pagination.goToPage,
        }}
        sorting={{
          field: filters.sortField,
          direction: filters.sortDirection,
          onSort: actions.handleSort,
        }}
        emptyMessage="No rooms found matching your criteria"
      />

      {error && (
        <div className="py-4 text-center text-red-600">
          Error loading rooms: {error.message}
        </div>
      )}
    </div>
  );
};
